<script setup lang="ts">
  import { decodeHTML } from "entities";
  import { computed, defineComponent, getCurrentInstance, h } from "vue";
  import type { CSSProperties, VNode, VNodeArrayChildren } from "vue";
  import { useUrlResolver } from "#imports";
  import { getOptionsFromNode } from "@shopware/cms-base-layer/helpers/html-to-vue/getOptionsFromNode";
  import type { NodeObject } from "@shopware/cms-base-layer/helpers/html-to-vue/getOptionsFromNode";
  import { renderHtml } from "@shopware/cms-base-layer/helpers/html-to-vue/renderToHtml";
  type RawChildren = string | number | boolean | VNode | VNodeArrayChildren;

  const props = defineProps < {
    content: { data: { text: string } };
    isShowroomTabSection?: boolean;
    isFaqSection?: boolean;
  } > ();
  const context = getCurrentInstance();

  const mappedContent = computed < string > (() => {
    return props.content?.data.text;
  });

  function removeInlineStyles(html: string): string {
    // Removes all style="..." attributes from HTML tags
    return html.replace(/ style="[^"]*"/gi, "");
  }

  function buildNodeAttrs(
    node: NodeObject,
    resolveUrl: (url: string) => string,
    extraClass: string
  ) {
    const options = getOptionsFromNode(node, resolveUrl);

    const mergedClass = [
      options.attrs?.class,
      (node as any).class,
      extraClass,
    ]
      .filter(Boolean)
      .join(" ");

    return {
      ...options.attrs,
      ...(mergedClass ? { class: mergedClass } : {}),
      ...(typeof (node as any).id === 'string' ? { id: (node as any).id } : {}),
    };
  }

  const CmsTextRender = defineComponent({
    setup() {
      const { resolveUrl } = useUrlResolver();

      const config = {
        textTransformer: (text: string) => decodeHTML(text),
        extraComponentsMap: {
          link: {
            conditions(node: NodeObject) {
              return (
                node.type === "tag" &&
                node.name === "a" &&
                !node.attrs?.class?.match(/btn\s?/)
              );
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              const options = getOptionsFromNode(node, resolveUrl);
              const href = options.attrs?.href || '';
              const isExternal = href.startsWith('http') || href.startsWith('//') || href.startsWith('mailto:') || href.startsWith('tel:');
              const isHashLink = href.startsWith('#');

              // Add data attributes for client-side handling
              const attrs: Record<string, any> = {
                ...options.attrs,
                class: "underline text-base font-normal text-primary hover:text-secondary-900",
              };

              // Only add data-internal-link for non-external, non-hash links
              if (!isExternal && !isHashLink) {
                attrs['data-internal-link'] = 'true';
              }

              // Add onClick handler for hash links to scroll to target
              if (isHashLink) {
                attrs.onClick = (e: Event) => {
                  e.preventDefault();
                  const targetId = href.substring(1);
                  const targetElement = document.getElementById(targetId);
                  if (targetElement) {
                    const elementPosition = targetElement.getBoundingClientRect().top;
                    // Use 200 offset for large screens (iMac), 100 for smaller screens
                    const offset = window.innerWidth > 1920 ? 180 : (window.innerWidth > 1024 ? 100 : 50);
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth',
                    });
                  }
                };
              }

              return createElement(
                "a",
                attrs,
                [...children],
              );
            },
          },
          button: {
            conditions(node: NodeObject) {
              return (
                node.type === "tag" &&
                node.name === "a" &&
                node.attrs?.class?.match(/btn\s?/)
              );
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              let _class = "";
              if (node?.attrs?.class) {
                const btnClass =
                  "rounded-md inline-block my-2 py-2 px-4 border border-transparent text-sm font-medium focus:outline-none disabled:opacity-75";

                _class = node.attrs.class
                  .replace("btn-secondary", `${btnClass} bg-dark text-white`)
                  .replace("btn-primary", `${btnClass} bg-primary text-white`);
              }

              const options = getOptionsFromNode(node, resolveUrl);
              const href = options.attrs?.href || '';
              const isExternal = href.startsWith('http') || href.startsWith('//') || href.startsWith('mailto:') || href.startsWith('tel:');

              // Add data attributes for client-side handling
              const attrs: Record<string, any> = {
                ...options.attrs,
                class: _class,
              };

              if (!isExternal) {
                attrs['data-internal-link'] = 'true';
              }

              return createElement(
                "a",
                attrs,
                [...children],
              );
            },
          },
          font: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "font";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              // convert from <font color="#ce0000">Headline 1</font> to <span style="color:#ce0000">Headline 1</span>
              let newStyle = null;
              const styleColor = node?.attrs?.color;
              if (styleColor && node.attrs) {
                const currentStyle = node.attrs?.style ?? "";
                newStyle = `color:${styleColor};${currentStyle}`;
                const { color: _, ...attrsWithoutColor } = node.attrs;
                node.attrs = attrsWithoutColor;
              }

              return createElement(
                "span",
                {
                  style: newStyle,
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          section: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "section";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "section",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          img: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "img";
            },
            renderer(node: NodeObject, children: RawChildren[], createElement: typeof h) {
              return createElement(
                "img",
                buildNodeAttrs(node, resolveUrl, "")
              );
            },
          },

          ul: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "ul";
            },
            renderer(node: NodeObject, children: RawChildren[], createElement: typeof h) {
              let ulClass = "";

              if (props.isShowroomTabSection) {
                ulClass = "md:columns-2 space-y-1";
              }

              if (props.isFaqSection) {
                ulClass += " list-disc list-inside space-y-2";
              }

              return createElement(
                "ul",
                buildNodeAttrs(node, resolveUrl, ulClass),
                children
              );
            },
          },


          li: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "li";
            },
            renderer(node: NodeObject, children: RawChildren[], createElement: typeof h) {
              return createElement(
                "li",
                buildNodeAttrs(node, resolveUrl, ""),
                children
              );
            },
          },

          ol: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "ol";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "ol",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          h1: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "h1";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              const options = getOptionsFromNode(node, resolveUrl);
              const attrs: Record<string, any> = {
                ...options.attrs,
              };

              // Preserve id attribute if it exists
              if (node.attrs && 'id' in node.attrs && node.attrs.id) {
                attrs.id = node.attrs.id as string;
              }

              return createElement(
                "h1",
                attrs,
                [...children],
              );
            },
          },
          h2: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "h2";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              const options = getOptionsFromNode(node, resolveUrl);
              const attrs: Record<string, any> = {
                ...options.attrs,
              };

              // Preserve id attribute if it exists
              if (node.attrs && 'id' in node.attrs && node.attrs.id) {
                attrs.id = node.attrs.id as string;
              }

              return createElement(
                "h2",
                attrs,
                [...children],
              );
            },
          },
          h3: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "h3";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              const options = getOptionsFromNode(node, resolveUrl);
              const attrs: Record<string, any> = {
                ...options.attrs,
              };

              // Preserve id attribute if it exists
              if (node.attrs && 'id' in node.attrs && node.attrs.id) {
                attrs.id = node.attrs.id as string;
              }

              return createElement(
                "h3",
                attrs,
                [...children],
              );
            },
          },
          h4: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "h4";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              const options = getOptionsFromNode(node, resolveUrl);
              const attrs: Record<string, any> = {
                ...options.attrs,
              };

              // Preserve id attribute if it exists
              if (node.attrs && 'id' in node.attrs && node.attrs.id) {
                attrs.id = node.attrs.id as string;
              }

              return createElement(
                "h4",
                attrs,
                [...children],
              );
            },
          },
          h5: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "h5";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              const options = getOptionsFromNode(node, resolveUrl);
              const attrs: Record<string, any> = {
                ...options.attrs,
              };

              // Preserve id attribute if it exists
              if (node.attrs && 'id' in node.attrs && node.attrs.id) {
                attrs.id = node.attrs.id as string;
              }

              return createElement(
                "h5",
                attrs,
                [...children],
              );
            },
          },
          h6: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "h6";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              const options = getOptionsFromNode(node, resolveUrl);
              const attrs: Record<string, any> = {
                ...options.attrs,
              };

              // Preserve id attribute if it exists
              if (node.attrs && 'id' in node.attrs && node.attrs.id) {
                attrs.id = node.attrs.id as string;
              }

              return createElement(
                "h6",
                attrs,
                [...children],
              );
            },
          },
          p: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "p";
            },
            renderer(node: NodeObject, children: RawChildren[], createElement: typeof h) {
              return createElement(
                "p",
                buildNodeAttrs(node, resolveUrl, ""),
                children
              );
            },
          },

          span: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "span";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "span",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          br: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "br";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "br",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
              );
            },
          },
          hr: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "hr";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "hr",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
              );
            },
          },
          strong: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "strong";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "strong",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          b: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "b";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "b",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          i: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "i";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "i",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          em: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "em";
            },
            renderer(
              node: NodeObject,
              children: RawChildren[],
              createElement: typeof h,
            ) {
              return createElement(
                "em",
                {
                  ...getOptionsFromNode(node, resolveUrl).attrs,
                },
                [...children],
              );
            },
          },
          div: {
            conditions(node: NodeObject) {
              return node.type === "tag" && node.name === "div";
            },
            renderer(node: NodeObject, children: RawChildren[], createElement: typeof h) {
              return createElement(
                "div",
                buildNodeAttrs(node, resolveUrl, ""),
                children
              );
            },
          },

        },
      };
      const rawHtml =
        mappedContent.value?.length > 0
          ? removeInlineStyles(mappedContent.value)
          : "<div class='cms-element-text missing-content-element'></div>";

      return () =>
        h("div", {}, renderHtml(rawHtml, config, h, context, resolveUrl));
    },
  });
</script>

<template>
  <CmsTextRender />
</template>